import React, { useState, useMemo, useRef } from 'react';
import s from './PromptBlock.module.css';
import CardItem from '../../../onboarding-page/entities/card-item/CardItem';
import { useSelector } from 'react-redux';
import { selectCurrentProject } from '../../../../store/selectors/projectSelectors';
import Button from '../../../../shared/ui/button/Button';
import { useAppDispatch } from '../../../../store/store';
import PremiumFeatureTooltip from '../premium-feature-tooltip/PremiumFeatureTooltip';
import { generateContent, updateProject } from '../../../../shared/api/textProjectApi';
import { toast } from 'react-toastify';
const voiceOptions = [
    {
        text: 'Hustle',
        tag: 'hustle',
        color: '#f66731',
        tooltip: (
            <p>
                A voice that emphasizes hard work, ambition, and relentless drive. It’s energetic,
                no-nonsense, and encourages a go-getter mentality.
            </p>
        ),
    },
    {
        text: 'Glow-up',
        tag: 'glow-up',
        color: '#9ef344',
        tooltip: (
            <p>
                A tone that focuses on transformation, self-improvement, and positive change. It’s
                inspirational, empowering, and celebrates personal or brand evolution.
            </p>
        ),
    },
    {
        text: 'Buddy',
        tag: 'buddy',
        color: '#40E0D0',
        tooltip: (
            <p>
                A friendly, approachable, and casual voice that feels like chatting with a close
                friend. It’s warm, supportive, and easygoing.
            </p>
        ),
    },
    {
        text: 'Jester',
        tag: 'jester',
        color: '#FF6347',
        tooltip: (
            <p>
                A playful, witty, and humorous tone that doesn’t take itself too seriously. It’s all
                about entertaining and bringing joy.
            </p>
        ),
    },
    {
        text: 'Sparks',
        tag: 'sparks',
        color: '#ADFF2F',
        tooltip: (
            <p>
                A voice that ignites creativity, excitement, and fresh ideas. It’s energetic,
                inspiring, and encourages new possibilities.
            </p>
        ),
    },
    {
        text: 'Dreams',
        tag: 'dreams',
        color: '#87CEEB',
        tooltip: (
            <p>
                A tone that is aspirational, hopeful, and imaginative. It emphasizes big visions,
                possibilities, and achieving the impossible.
            </p>
        ),
    },
    {
        text: 'Hype',
        tag: 'hype',
        color: '#FF69B4',
        tooltip: (
            <p>
                An excitable, bold, and enthusiastic voice designed to build anticipation and buzz
                around people, products, or events.
            </p>
        ),
    },
    {
        text: 'Pride',
        tag: 'pride',
        color: '#b05fff',
        tooltip: (
            <p>
                A confident, bold, and celebratory tone that highlights identity, achievements, and
                a sense of belonging.
            </p>
        ),
    },
    {
        text: 'Muse',
        tag: 'muse',
        color: '#9370DB',
        tooltip: (
            <p>
                A thoughtful, inspiring, and creative voice that encourages imagination and sparks
                new ideas.
            </p>
        ),
    },
    {
        text: 'Quirk',
        tag: 'quirk',
        color: '#00FA9A',
        tooltip: (
            <p>
                A fun, offbeat, and playfully unconventional tone that celebrates uniqueness and
                stands out from the crowd.
            </p>
        ),
    },
    {
        text: 'Visionary',
        tag: 'visionary',
        color: '#8A2BE2',
        tooltip: (
            <p>
                A bold, future-focused, and inspirational voice that emphasizes innovation, big
                ideas, and long-term impact.
            </p>
        ),
    },
    {
        text: 'Support',
        tag: 'support',
        color: '#FFDAB9',
        tooltip: (
            <p>
                A caring, helpful, and reassuring voice that prioritizes guidance, customer care,
                and reliability.
            </p>
        ),
    },
    {
        text: 'Pro-tip',
        tag: 'pro-tip',
        color: '#5dabf6',
        tooltip: (
            <p>
                A practical, knowledgeable, and expert voice that delivers actionable advice and
                useful insights.
            </p>
        ),
    },
    {
        text: 'Solid',
        tag: 'solid',
        color: '#f4aff1',
        tooltip: (
            <p>
                A reliable, trustworthy, and straightforward tone that emphasizes stability,
                strength, and consistency.
            </p>
        ),
    },
    {
        text: 'Maverick',
        tag: 'maverick',
        color: '#DC143C',
        tooltip: (
            <p>
                A bold, rebellious, and unconventional voice that challenges norms and promotes
                independent thinking.
            </p>
        ),
    },
    {
        text: 'Mentor',
        tag: 'mentor',
        color: '#20B2AA',
        tooltip: (
            <p>
                A wise, nurturing, and experienced voice that offers guidance, personal development,
                and supportive wisdom.
            </p>
        ),
    },
    {
        text: 'Sage',
        tag: 'sage',
        color: '#2E8B57',
        tooltip: (
            <p>
                A calm, authoritative, and timeless voice that conveys deep knowledge and profound
                insights.
            </p>
        ),
    },
    {
        text: 'Dynamo',
        tag: 'dynamo',
        color: '#1E90FF',
        tooltip: (
            <p>
                An energetic, bold, and unstoppable tone that emphasizes action, momentum, and
                continuous progress.
            </p>
        ),
    },
];

const VoiceBlock = () => {
    const dispatch = useAppDispatch();
    const currentProject = useSelector(selectCurrentProject);
    const currentVoice = currentProject?.voice || '';

    const [selectedVoice, setSelectedVoice] = useState<string>(currentVoice);

    const hasChanges = useMemo(() => selectedVoice !== currentVoice, [selectedVoice, currentVoice]);

    const handleOptionSelect = (tag: string) => {
        setSelectedVoice((prev) => (prev === tag ? '' : tag));
    };
    const updateVoice = async () => {
        await dispatch(updateProject({ ...currentProject, voice: selectedVoice }));
        await handleGenerateScript();
    };

    const handleGenerateScript = async () => {
        if (currentProject?.id) {
            try {
                const response = await dispatch(
                    generateContent({ projectId: currentProject.id, kind: 'text_only' }),
                ).unwrap();
                console.log('Script generated successfully:', response);
            } catch (error: any) {
                console.error('Failed to handle generate script:', error);
                toast.error(error?.message || 'Failed to handle generate script');
            }
        }
    };

    const voicesPerPage = 6;
    const groupedVoices = useMemo(
        () =>
            Array.from({ length: Math.ceil(voiceOptions.length / voicesPerPage) }, (_, i) =>
                voiceOptions.slice(i * voicesPerPage, i * voicesPerPage + voicesPerPage),
            ),
        [],
    );

    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentPage, setCurrentPage] = useState(0);

    const handleScroll = () => {
        if (!scrollRef.current) return;
        const scrollLeft = scrollRef.current.scrollLeft;
        const width = scrollRef.current.offsetWidth;
        const page = Math.round(scrollLeft / width);
        setCurrentPage(page);
    };

    return (
        <div className={s.container}>
            <div className={s.block_container}>
                <div className={s.header_container}>
                    <h3 className={s.bold_header}>Adjust your brand voice</h3>
                    <p className={s.text}>Choose how unique you want your content to be</p>
                </div>
                <div className={s.tone_options}>
                    {voiceOptions.map((option) => (
                        <CardItem
                            key={option.tag}
                            item={{
                                ...option,
                                isActive: selectedVoice.includes(option.tag),
                            }}
                            onClick={() => handleOptionSelect(option.tag)}
                            style={{ borderRadius: '5px', fontSize: '10px', height: '35px' }}
                            activeStyle={{ background: option.color }}
                        />
                    ))}
                </div>

                {/* Mobile Swipeable Carousel */}
                <div className={s.tone_carousel_wrapper}>
                    <div className={s.tone_carousel} ref={scrollRef} onScroll={handleScroll}>
                        {groupedVoices.map((group, i) => (
                            <div className={s.tone_page_voice} key={i}>
                                {group.map((option) => (
                                    <CardItem
                                        key={option.tag}
                                        item={{
                                            ...option,
                                            isActive: selectedVoice.includes(option.tag),
                                        }}
                                        onClick={() => handleOptionSelect(option.tag)}
                                        style={{
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            height: 'auto',
                                        }}
                                        activeStyle={{ background: option.color }}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className={s.tone_dots}>
                        {groupedVoices.map((_, i) => (
                            <div
                                key={i}
                                className={`${s.dot} ${i === currentPage ? s.active : ''}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div className={s.block_container}>
                <p className={s.text}>
                    Craft your brand’s voice: Tailor the AI’s tone, style, and vocabulary.
                </p>
                <div className={`${s.pro_features}`}>
                    <p>Customize brand voice</p>
                    <PremiumFeatureTooltip />
                </div>

                {hasChanges && (
                    <div style={{ marginTop: 100 }}>
                        <Button
                            onClick={updateVoice}
                            text={'Confirm Selection'}
                            style={{
                                background: '#FFDD35',
                                borderRadius: 4,
                                width: '90%',
                                margin: '0 auto',
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceBlock;
